import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReactHighcharts from 'react-highcharts';
import Moment from 'moment';
import { extendMoment } from 'moment-range';
import numeral from 'numeral';
import pluralize from 'pluralize';
import { groupBy, map, orderBy, uniq,
  first, findIndex, reduce, each } from 'lodash';

import PeriodDropdown from '../rate-graph/period-dropdown';
import PeriodForm from '../rate-graph/period-form';
import { getColorFromKey } from '../../../../util/helpers';
import { getHasNoDecisionSetup } from '../../../../components/search-form/search-form-reducer';
import { format as formatMoney } from '../../../../components/money-display';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import { getIsFontSizeBig } from '../../../App/AppReducer';

// Import Selectors
import { getOpportunities, getLoaded, getLoading } from '../../AnalysisReducer';

const moment = extendMoment(Moment);

const USER_PREFERENCE_PERIOD_TYPE = 'pursuit-time-graph-period-type';
const USER_PREFERENCE_PERIOD_DAYS = 'pursuit-time-graph-period-days';

class PursuitLengthOvertimeGraph extends Component {
  constructor(props) {
    super(props);
    this.state = {
      period: 'monthly',
      days: null,
      modalOpen: false,
    };
    this.handlePeriodChange = this.handlePeriodChange.bind(this);
    this.handleCustomPeriodSelect = this.handleCustomPeriodSelect.bind(this);
    this.handlePeriodDayChange = this.handlePeriodDayChange.bind(this);
  }

  componentWillMount() {
    const { userPreferences } = this.context;
    const period = userPreferences[USER_PREFERENCE_PERIOD_TYPE] || 'monthly';
    const days = userPreferences[USER_PREFERENCE_PERIOD_DAYS] || 1;
    this.setState({
      period, days,
    });
  }

  handlePeriodChange(period) {
    this.setState({ period, days: null });
    this.context.updateUserPreference(USER_PREFERENCE_PERIOD_TYPE, period);
    this.context.updateUserPreference(USER_PREFERENCE_PERIOD_DAYS, null);
  }

  handleCustomPeriodSelect() {
    this.setState({ modalOpen: true });
  }

  handlePeriodDayChange(days) {
    this.setState({ days, period: 'days' });
    this.context.updateUserPreference(USER_PREFERENCE_PERIOD_TYPE, 'days');
    this.context.updateUserPreference(USER_PREFERENCE_PERIOD_DAYS, days);
  }

  renderGraph() {
    const {
      loaded, loading, opportunities, hasNoDecisionSetup, organization, closeDateRange,
      isFontSizeBig,
    } = this.props;
    if (!loaded || loading || opportunities.length === 0) {
      return null;
    }
    let suffix = '';
    let symbol = '';
    if (organization) {
      suffix = organization.currency_suffix;
      symbol = organization.currency_symbol;
    }
    const {
      period, days,
    } = this.state;
    // group opportunities
    let format = null;
    if (period === 'monthly') {
      format = 'YYYYMM';
    } else if (period === 'quarterly') {
      format = 'YYYYQ';
    } else if (period === 'annual') {
      format = 'YYYY';
    }

    const inputOpportunities = orderBy(opportunities.map((opportunity) => {
      opportunity.close_date = moment(opportunity.close_date, 'YYYY-MM-DD').startOf('day');
      return opportunity;
    }), ['close_date', 'asc']);


    let groupedOpportunities = {};
    if (closeDateRange && closeDateRange.length > 1 && closeDateRange[0] && closeDateRange[1]) {
      let mRange = moment.range(closeDateRange[0], closeDateRange[1]);
      if (period === 'monthly') {
        mRange = moment.range(moment(closeDateRange[0]).startOf('month'), moment(closeDateRange[1]).endOf('month'));
      } else if (period === 'quarterly') {
        mRange = moment.range(moment(closeDateRange[0]).startOf('quarter'), moment(closeDateRange[1]).endOf('quarter'));
      } else if (period === 'annual') {
        mRange = moment.range(moment(closeDateRange[0]).startOf('year'), moment(closeDateRange[1]).endOf('year'));
      }
      let rIt = 'days';
      let step = days;
      if (period === 'monthly') {
        rIt = 'months';
        step = 1;
      } else if (period === 'quarterly') {
        rIt = 'quarters';
        step = 1;
      } else if (period === 'annual') {
        rIt = 'years';
        step = 1;
      }

      const labelRange = Array.from(mRange.by(rIt, { exclusive: false, step }));
      const xLabels = labelRange.map((m, i) => moment.range(m, m.clone().add(step, rIt).subtract(i === labelRange.length - 1 ? 0 : 1, 'days')));
      groupedOpportunities = reduce(xLabels, (items, l) => {
        const k = l.start.format('YYYY-MM-DD');
        items[k] = [];
        return items;
      }, {});
      each(inputOpportunities, (opportunity) => {
        if (opportunity.close_date) {
          const idx = findIndex(xLabels, xl => xl.contains(opportunity.close_date));
          if (idx >= 0) {
            groupedOpportunities[xLabels[idx].start.format('YYYY-MM-DD')].push(opportunity);
          }
        }
      });
    } else if (format) {
      groupedOpportunities = format ? groupBy(inputOpportunities, opportunity => (opportunity.close_date ? opportunity.close_date.format(format) : null)) : {};
    } else if (period === 'days' && days !== null && days !== 0) {
      const minDateOpportunities = inputOpportunities[0];
      const start = minDateOpportunities.close_date;
      groupedOpportunities = groupBy(inputOpportunities, (opportunity) => {
        const d = opportunity.close_date.diff(start, 'days');
        return Math.floor(d / days);
      });
    }

    groupedOpportunities = orderBy(map(groupedOpportunities, (groupedOpportunity, gPeriod) => {
      const items = reduce(groupBy(groupedOpportunity, opportunity => opportunity.stage), (result, value, stage) => {
        // TODO: put in one single loop to calculate: sum, min, max
        let pursuitLengthSum = 0;
        let pursuitLengthMin = null;
        let pursuitLengthMax = 0;
        let amount = 0;
        let repIds = [];
        const opportunityIds = [];
        const count = value.length;
        each(value, (v) => {
          const p = v.pursuit_length || 0;
          pursuitLengthSum += p;
          amount += v.amount || 0;
          repIds.push(v.owner_id);
          opportunityIds.push(v.opportunity_id);
          pursuitLengthMax = Math.max(pursuitLengthMax, p);
          if (pursuitLengthMin === null) {
            pursuitLengthMin = p;
          } else {
            pursuitLengthMin = Math.min(pursuitLengthMin, p);
          }
        });
        repIds = uniq(repIds);
        result[stage] = {
          pursuitLengthSum,
          pursuitLengthMin,
          pursuitLengthMax,
          amount,
          opportunityIds,
          count,
          repCount: repIds.length,
          averagePursuitLength: value.length > 0 ? pursuitLengthSum / value.length : 0,
        };
        return result;
      }, {});
      const closeDate = groupedOpportunity.length ? first(groupedOpportunity).close_date : moment(gPeriod, 'YYYY-MM-DD');
      const result = {
        period: gPeriod,
        items,
        closeDate,
      };
      return result;
    }), ['period', 'asc']);


    const series = [
      {
        name: 'Won', color: getColorFromKey('won'), alias: 'Wins', data: [], marker: { symbol: 'circle' },
      },
      {
        name: 'Lost', color: getColorFromKey('lost'), alias: 'Losses', data: [], marker: { symbol: 'circle' },
      },
    ];
    if (hasNoDecisionSetup) {
      series.push({
        name: 'No Decision', color: getColorFromKey('noDecision'), alias: 'No Decisions', data: [], marker: { symbol: 'circle' },
      });
    }

    const categories = map(groupedOpportunities, (groupedOpportunity) => {
      let f = 'll';
      if (period === 'monthly') {
        f = 'MMM YYYY';
      } else if (period === 'quarterly') {
        f = '[Q]Q YYYY';
      } else if (period === 'annual') {
        f = 'YYYY';
      }
      each(series, (s) => {
        const stage = s.name;
        let y = 0;
        let shortest = 0;
        let longest = 0;
        let amount = 0;
        let count = 0;
        let repCount = 0;
        if (groupedOpportunity.items[stage]) {
          y = (groupedOpportunity.items[stage].averagePursuitLength || 0);
          shortest = (groupedOpportunity.items[stage].pursuitLengthMin || 0);
          longest = (groupedOpportunity.items[stage].pursuitLengthMax || 0);
          amount = (groupedOpportunity.items[stage].amount || 0);
          count = (groupedOpportunity.items[stage].count || 0);
          repCount = (groupedOpportunity.items[stage].repCount || 0);
        }
        const avgUnit = y < 30 ? 'day' : 'month';
        const avgVal = y < 30 ? y : y / 30;
        const avgFormat = y < 30 ? '0,0' : '0,0.0';
        const averageLength = `${numeral(avgVal).format(avgFormat)} ${pluralize(avgUnit, avgVal)}`;

        const shortUnit = shortest < 30 ? 'day' : 'month';
        const shortVal = shortest < 30 ? shortest : shortest / 30;
        const shortFormat = shortest < 30 ? '0' : '0,0.0';
        const shortestStr = `${numeral(shortVal).format(shortFormat)} ${pluralize(shortUnit, shortVal)}`;

        const longUnit = longest < 30 ? 'day' : 'month';
        const longVal = longest < 30 ? longest : longest / 30;
        const longFormat = longest < 30 ? '0' : '0,0.0';
        const longestStr = `${numeral(longVal).format(longFormat)} ${pluralize(longUnit, longVal)}`;

        const amountStr = `${formatMoney(amount, true, 1, symbol, suffix)}`;
        const win = stage === 'Won' ? 'won' : 'lost';

        const summaryStr = `${amountStr} ${win} in ${numeral(count).format('0,0')} ${pluralize('deal', count)}
          for ${numeral(repCount).format('0')} ${pluralize('rep', repCount)}`;

        s.data.push({
          y,
          averageLength,
          shortestStr,
          longestStr,
          summaryStr,
          opportunityIds: groupedOpportunity.items[stage] ? groupedOpportunity.items[stage].opportunityIds : [],
          events: {
            click: (event) => {
              this.props.onPointSelect(event.point.options.opportunityIds);
            },
          },
        });
      });
      return groupedOpportunity.closeDate.format(f);
    });
    const config = {
      chart: {
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        backgroundColor: 'rgba(0, 0, 0, 0)',
        type: 'line',
      },
      credits: {
        enabled: false,
      },
      title: {
        text: null,
      },
      xAxis: {
        categories,
        crosshair: true,
        labels: {
          style: {
            fontSize: isFontSizeBig ? '16px' : null,
          },
        },
      },
      tooltip: {
        formatter: function fmt() {
          let str = '';
          let { category } = this.point;
          if (period === 'days' && days !== null && days !== 0) {
            category = `${days} Day Period`;
          }
          str += `<span style="color:${this.series.color};font-size:16px; font-weight: bold;">${category} ${this.series.options.alias}:</span>`;
          str += `<table class="table table-condensed" style="margin-bottom:0; margin-top: 1em; width: 100%; ${isFontSizeBig ? 'font-size:16px;' : ''}">
            <tr>
              <td>Avg. Length:&nbsp;</td>
              <td class="text-right"><b>${this.point.averageLength}</b></td>
            </tr>
            <tr>
              <td>Shortest:&nbsp;</td>
              <td class="text-right"><b>${this.point.shortestStr}</b></td>
            </tr>
            <tr>
              <td>Longest:&nbsp;</td>
              <td class="text-right"><b>${this.point.longestStr}</b></td>
            </tr>
            <tr>
              <td class="text-center" colspan="2">${this.point.summaryStr}</td>
            </tr>
          `;
          return str;
        },
        useHTML: true,
        backgroundColor: 'rgba(0,0,0, 0.75)',
        borderColor: 'rgba(0,0,0, 0.95)',
        style: {
          color: 'rgba(255, 255, 255, 0.75)',
        },
      },
      legend: {
        itemStyle: {
          fontSize: isFontSizeBig ? '16px' : null,
        },
      },
      yAxis: {
        title: {
          text: 'Pursuit Days',
          style: {
            fontSize: isFontSizeBig ? '16px' : null,
          },
        },
        tickAmount: 9,
        labels: {
          formatter: function f() {
            let v = 0;
            if (this.value !== 0) {
              v = numeral(this.value).format('0,0');
            }
            return v;
          },
          style: {
            fontSize: isFontSizeBig ? '16px' : null,
          },
        },
        alternateGridColor: 'rgba(66,66,66,0.05)',
        lineWidth: 0,
        plotLines: [{
          value: 0,
          dashStyle: 'dashed',
          color: 'rgba(0, 0, 0, 0.5)',
          width: 2,
          label: {
            text: null,
          },
        }],
      },
      plotOptions: {
        series: {
          marker: {
            fillColor: '#ffffff',
            lineWidth: 2,
            radius: 5,
            lineColor: null, // inherit from series
          },
        },
      },
      series,
    };

    return (
      <div>
        <ReactHighcharts config={config} isPureConfig />
      </div>
    );
  }

  render() {
    const {
      loaded, loading, opportunities,
    } = this.props;
    if (!loaded || loading || opportunities.length === 0) {
      return null;
    }
    const {
      period, modalOpen, days,
    } = this.state;

    return (
      <div>
        <div className="animated fadeIn">
          <div>
            <h5 className="sub-header-with-controls">
              View average opportunity pursuit time by
              &nbsp;
              <PeriodDropdown
                period={period}
                days={days}
                onChange={this.handlePeriodChange}
                onCustomPeriodSelect={this.handleCustomPeriodSelect}
              />
              &nbsp;
              periods
            </h5>
          </div>
          {this.renderGraph()}
        </div>
        <PeriodForm
          maxValue={opportunities.length}
          modalOpen={modalOpen}
          onDayChange={this.handlePeriodDayChange}
          onHide={() => { this.setState({ modalOpen: false }); }}
        />
      </div>
    );
  }
}

PursuitLengthOvertimeGraph.propTypes = {
  opportunities: PropTypes.arrayOf(PropTypes.shape({
    opportunity_id: PropTypes.string.isRequired,
  })).isRequired,
  organization: PropTypes.shape({
    organization_id: PropTypes.string.isRequired,
  }),
  loaded: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  hasNoDecisionSetup: PropTypes.bool.isRequired,
  closeDateRange: PropTypes.arrayOf(PropTypes.instanceOf(moment)),
  onPointSelect: PropTypes.func,
  isFontSizeBig: PropTypes.bool.isRequired,
};

PursuitLengthOvertimeGraph.defaultProps = {
  organization: null,
  closeDateRange: [],
  onPointSelect: () => {},
};

PursuitLengthOvertimeGraph.contextTypes = {
  updateUserPreference: PropTypes.func.isRequired,
  userPreferences: PropTypes.object.isRequired,
};

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    opportunities: getOpportunities(state),
    organization: getUserCurrentOrganization(state),
    loaded: getLoaded(state),
    loading: getLoading(state),
    hasNoDecisionSetup: getHasNoDecisionSetup(state),
    isFontSizeBig: getIsFontSizeBig(state),
  };
}

export default connect(mapStateToProps)(PursuitLengthOvertimeGraph);
